import { AuthenticationCommandResponses } from "./models/authenticationCommandResponse";
import { AuthenticationCommands } from "./models/authenticationCommands";
import { AuthService, authResponseCodeEnum } from "../services/auth";
import { CommandResponse } from "./models/commandResponse";
import { ClientState } from "./clientState";
import { verifyTokens } from "./utils";
import { isNullOrEmpty } from "../utils/strings";
import ClientPortals from "../config/clientPortals.json";
import { Logger } from "../utils/logging";

/**
 * Processes authentication messages
 * @param {object} message received from parent window for authentication processing
 * @returns {Promise}
 */
export const processMessage = async (message) => {
  if (!validateMessage(message)) {
    return;
  }

  const clientType = message.data?.clientType;
  const { command, commandDetails } = message.data;
  const {
    id,
    authentication,
    redirectUnauthenticated,
    redirectUri,
    clientState,
  } = commandDetails;
  if (command) {
    Logger.debug("processMessage", message.data);
  }

  let response;
  switch (command) {
    case AuthenticationCommands.Initialize:
      response = processInitialize();
      break;
    case AuthenticationCommands.CheckAuthentication:
      response = await processCheckAuthentication(authentication, clientType);
      break;
    case AuthenticationCommands.ChangePassword:
      const { currentPassword, newPassword, logout } = commandDetails;
      response = await processChangePassword(
        currentPassword,
        newPassword,
        logout,
        authentication,
        clientType
      );
      break;
    case AuthenticationCommands.Logout:
      const { clientOnlyLogout } = commandDetails;
      response = processLogout(clientOnlyLogout);
      break;
    case AuthenticationCommands.RedeemCode:
      const { code, redirectUri, codeVerifier } = commandDetails;
      response = await processRedeemCode(
        clientType,
        redirectUri,
        code,
        codeVerifier
      );
      break;
    case AuthenticationCommands.RefreshTokens:
      response = await processRefreshTokens(
        clientType,
        authentication.refreshToken
      );
      break;
    case AuthenticationCommands.UpdateProfile:
      const { user } = commandDetails;
      response = await processUpdateProfile(clientType, user, authentication);
      break;
    default:
      return;
  }

  if (response) {
    response.responseDetails = {
      ...response.responseDetails,
      id,
      clientState, //encoded ClientState
    };
    Logger.debug("processMessage - response", response);
    window.parent.postMessage(response, "*");
  }

  if (
    redirectUnauthenticated === true &&
    response?.responseDetails?.isAuthenticated === false &&
    !isNullOrEmpty(redirectUri)
  ) {
    redirectToLoginPage(id, clientState);
  }
};

/**
 * Processes 'change-password' commands: https://flash-team.atlassian.net/wiki/spaces/ENG/pages/3675389953/Unified+Login+Authentication+Components+API#change-password
 * @param {string} currentPassword - users current password
 * @param {string} newPassword - users new password
 * @param {boolean} logout - indicates that the user should be logged out after password change
 * @param {oAuthTokenCollection} authentication - contains a map of authentication tokens { accessToken, idToken, refreshToken }
 * @param {string} clientType - the client type for the portal
 * @returns {object} Command 'change-password-response' response object
 */
const processChangePassword = async (
  currentPassword,
  newPassword,
  logout,
  authentication,
  clientType
) => {
  Logger.debug("clientCommService.processChangePassword");
  let response;
  try {
    const verifiedTokens = await verifyTokens(authentication, clientType);
    await AuthService.changePassword(
      currentPassword,
      newPassword,
      verifiedTokens.accessToken
    );
    if (logout === true) {
      Logger.debug("clientCommService.processChangePassword", "logging out");
      AuthService.logout();
      response = new CommandResponse(
        AuthenticationCommandResponses.ChangePassword,
        {
          isAuthenticated: false,
          success: true,
        }
      );
    } else {
      const authenticated = !isNullOrEmpty(verifiedTokens?.accessToken);
      const currentUser = await ClientState.getCurrentUser(verifiedTokens);
      response = new CommandResponse(
        AuthenticationCommandResponses.ChangePassword,
        {
          isAuthenticated: authenticated,
          authentication: verifiedTokens,
          currentUser,
          success: true,
        }
      );
    }
  } catch (err) {
    response = new CommandResponse(
      AuthenticationCommandResponses.ChangePassword,
      {
        isAuthenticated: false,
        success: false,
        error: err,
      }
    );
  }
  return response;
};

const processInitialize = () => {
  return new CommandResponse(AuthenticationCommandResponses.Initialized, {
    success: true,
    isAuthenticated: false,
    clientPortals: ClientPortals,
  });
};

/**
 * Processes 'authentication-check' commands: https://flash-team.atlassian.net/wiki/spaces/ENG/pages/3675389953/Unified+Login+Authentication+Components+API#check-authentication
 * @param {oAuthTokenCollection} authentication - contains a map of authentication tokens { accessToken, idToken, refreshToken }
 * @param {string} clientType - the client type for the portal
 * @returns {object} Command 'check-authentication-response' response object
 */
const processCheckAuthentication = async (authentication, clientType) => {
  Logger.debug("clientCommService.processCheckAuthentication", clientType);
  let response;
  try {
    const verifiedTokens = await verifyTokens(authentication, clientType);
    const isAuthenticated = !isNullOrEmpty(verifiedTokens?.accessToken);
    const currentUser = await ClientState.getCurrentUser(verifiedTokens);

    response = new CommandResponse(
      AuthenticationCommandResponses.CheckAuthentication,
      {
        isAuthenticated,
        authentication: verifiedTokens,
        currentUser,
        success: true,
      }
    );
  } catch (err) {
    AuthService.logout();
    response = new CommandResponse(
      AuthenticationCommandResponses.CheckAuthentication,
      {
        isAuthenticated: false,
        success: false,
        error: err,
      }
    );
  }
  return response;
};

/**
 * Process 'logout' commands: https://flash-team.atlassian.net/wiki/spaces/ENG/pages/3675389953/Unified+Login+Authentication+Components+API#logout
 * @param {boolean} clientOnlyLogout - Determines if the user is logged out of both the SSO and the client portal, or just the portal.
 * @returns {object} Command 'logout-response' response object
 */
const processLogout = (clientOnlyLogout) => {
  Logger.debug("clientCommService.processLogout");
  if (!clientOnlyLogout) {
    AuthService.logout();
  }
  return new CommandResponse(AuthenticationCommandResponses.Logout, {
    isAuthenticated: false,
    success: true,
  });
};

/**
 * Redeems a auth code for auth tokens
 * @param {string} clientType - the client type for the portal
 * @param {string} redirectUri - a registered uri for the client
 * @param {string} code
 * @param {string} codeVerifier - the original codeVerifier (PKCE key) used for the code challenge
 * @returns {CommandResponse}
 */
const processRedeemCode = async (
  clientType,
  redirectUri,
  code,
  codeVerifier
) => {
  Logger.debug("clientCommService.processRedeemCode", code);
  let response;
  try {
    const apiResult = await AuthService.getTokensForClient(
      clientType,
      redirectUri,
      code,
      codeVerifier
    );
    const isAuthenticated = apiResult.result === authResponseCodeEnum.logged_in;
    const currentUser = await ClientState.getCurrentUser(
      apiResult.authentication
    );
    response = new CommandResponse(AuthenticationCommandResponses.RedeemCode, {
      isAuthenticated: isAuthenticated,
      authentication: apiResult.authentication,
      currentUser,
      success: true,
    });
  } catch (error) {
    response = new CommandResponse(AuthenticationCommandResponses.RedeemCode, {
      isAuthenticated: false,
      success: false,
      error,
    });
  }
  Logger.debug("processRedeemCode", response);
  return response;
};

/**
 * Refreshes and returns tokens if necessary otherwise
 * @param {string} clientType - the client type for the portal
 * @param {string} refreshToken - The JWT Refresh token
 * @returns {CommandResponse}
 */
const processRefreshTokens = async (clientType, refreshToken) => {
  Logger.debug("clientCommService.processRefreshTokens");
  try {
    const result = await AuthService.refreshTokensForClient(
      clientType,
      refreshToken
    );
    if (!result.success || !result.authentication) {
      throw new Error("Missing authentication");
    }

    /** @type {oAuthTokenCollection} */
    const authentication = {
      ...result.authentication,
      refreshToken,
    };
    const currentUser = await ClientState.getCurrentUser(authentication);
    return new CommandResponse(AuthenticationCommandResponses.RefreshTokens, {
      isAuthenticated: authentication?.accessToken !== undefined,
      authentication,
      currentUser,
      success: authentication?.accessToken !== undefined,
    });
  } catch (error) {
    return new CommandResponse(AuthenticationCommandResponses.RefreshTokens, {
      isAuthenticated: false,
      success: false,
      error,
    });
  }
};

/**
 * Process 'update-profile' commands: https://flash-team.atlassian.net/wiki/spaces/ENG/pages/3675389953/Unified+Login+Authentication+Components+API#update-profile
 * @param {string} clientType - the client type for the portal
 * @param {UserProfile} user
 * @param {oAuthTokenCollection} authentication - contains a map of authentication tokens
 * @returns {CommandResponse} Command 'update-profile-response' response object
 */
const processUpdateProfile = async (clientType, user, authentication) => {
  Logger.debug(
    "clientCommService.processUpdateProfile",
    user,
    user === undefined
  );
  let response;
  try {
    let currentUser = await ClientState.getCurrentUser(authentication);

    if (user.phoneNumber) {
      user.phoneNumber = `+${user.phoneNumber.replace(/[^\d]/g, "")}`;
    }

    const smsMfa = user.smsMfa;
    delete user.smsMfa;
    if (smsMfa && smsMfa !== currentUser?.mfaPreference?.sms) {
      await AuthService.setUserMfaSms(
        smsMfa ?? false,
        smsMfa ?? false,
        authentication.accessToken
      );
    }

    await AuthService.updateUser(user, clientType, authentication.accessToken);
    currentUser = await ClientState.getCurrentUser(authentication);

    response = new CommandResponse(
      AuthenticationCommandResponses.UpdateProfile,
      {
        isAuthenticated: true,
        authentication,
        currentUser,
        success: true,
      }
    );
  } catch (err) {
    response = new CommandResponse(
      AuthenticationCommandResponses.UpdateProfile,
      {
        isAuthenticated: authentication?.accessToken !== undefined,
        success: false,
        error: err,
      }
    );
  }

  return response;
};

/**
 * Method that tells the client to redirect to login page
 */
const redirectToLoginPage = (id, clientState) => {
  const response = new CommandResponse(
    AuthenticationCommandResponses.RedirectToLogin,
    {
      id,
      isAuthenticated: false,
      clientState,
      success: true,
    }
  );
  Logger.debug("redirectToLoginPage", response);
  window.parent.postMessage(response, "*");
};

/**
 * Validates that the message is a valid command message: https://flash-team.atlassian.net/wiki/spaces/ENG/pages/3675389953/Unified+Login+Authentication+Components+API#Commands
 * @param {object} message received from the postMessage api
 * @returns {boolean} indicates that the message containing an authentication command and has a valid origin
 */
const validateMessage = (message) => {
  return !(
    typeof message?.data !== "object" ||
    !message.data.command ||
    (message?.origin === window.location.origin && message.source === window)
  );
};
